import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

export default function ImportTab() {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) return toast.error("Choose a file");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/users/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported ${data.created} users`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="velo-card p-6">
        <h3 className="text-lg text-white font-medium">Bulk import users</h3>
        <p className="text-xs text-slate-500 mt-1 mb-6">Upload CSV or Excel (.xlsx). Existing phones will be skipped.</p>
        <div className="space-y-4">
          <div className="velo-surface rounded-lg p-6 border-2 border-dashed border-white/10">
            <input data-testid="input-import-file" type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setFile(e.target.files && e.target.files[0])} className="text-slate-300 text-sm" />
            {file ? <div className="text-xs text-slate-400 mt-3">Selected: <span className="text-white">{file.name}</span> ({(file.size/1024).toFixed(1)} KB)</div> : null}
          </div>
          <Button data-testid="btn-import-submit" onClick={submit} disabled={busy || !file} className="w-full h-12 rounded-full bg-pink-500 hover:bg-pink-400 text-black font-medium">
            {busy ? "Importing…" : "Upload & import"}
          </Button>
        </div>
        {result ? (
          <div className="mt-6 p-4 velo-surface rounded-lg">
            <div className="text-sm text-emerald-400">{result.created} created · {result.skipped} skipped</div>
            {result.errors && result.errors.length > 0 ? (
              <div className="mt-3">
                <div className="text-xs text-amber-400 uppercase tracking-wider mb-1">Errors</div>
                {result.errors.map((e, i) => <div key={i} className="text-xs text-slate-400 font-mono">{e}</div>)}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="velo-card p-6">
        <h3 className="text-lg text-white font-medium">Template format</h3>
        <p className="text-xs text-slate-500 mt-1 mb-4">Required columns (case-insensitive)</p>
        <div className="velo-surface rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#150733]">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Column</th>
                <th className="px-4 py-3">Required</th>
                <th className="px-4 py-3">Values</th>
              </tr>
            </thead>
            <tbody className="font-mono text-xs">
              <tr className="border-t border-white/5"><td className="px-4 py-3 text-white">name</td><td className="px-4 py-3 text-emerald-400">Yes</td><td className="px-4 py-3 text-slate-400">Any text</td></tr>
              <tr className="border-t border-white/5"><td className="px-4 py-3 text-white">phone</td><td className="px-4 py-3 text-emerald-400">Yes</td><td className="px-4 py-3 text-slate-400">Unique</td></tr>
              <tr className="border-t border-white/5"><td className="px-4 py-3 text-white">role</td><td className="px-4 py-3 text-slate-500">No</td><td className="px-4 py-3 text-slate-400">customer / affiliate</td></tr>
              <tr className="border-t border-white/5"><td className="px-4 py-3 text-white">affiliate_type</td><td className="px-4 py-3 text-amber-400">If affiliate</td><td className="px-4 py-3 text-slate-400">See list below</td></tr>
              <tr className="border-t border-white/5"><td className="px-4 py-3 text-white">associate_firm</td><td className="px-4 py-3 text-slate-500">No</td><td className="px-4 py-3 text-slate-400">Free text</td></tr>
              <tr className="border-t border-white/5"><td className="px-4 py-3 text-white">referral_code_used</td><td className="px-4 py-3 text-slate-500">No</td><td className="px-4 py-3 text-slate-400">Existing referral code</td></tr>
            </tbody>
          </table>
        </div>
        <div className="mt-6 text-xs text-slate-500 leading-relaxed">
          Tip: New users start with 0 points and get a unique referral base auto-generated.<br />
          <span className="text-slate-400 mt-2 block">Allowed <span className="font-mono">affiliate_type</span> values: Dealership Sales Personnel, Dealership Service Personnel, Auto Consultant, Car Loan Consultant, Car Insurance Consultant, Vision Partner, Driver, Other.</span>
        </div>
      </div>
    </div>
  );
}
